import {
    ChangeDetectionStrategy,
    Component,
    Inject,
    Input,
    OnInit,
    forwardRef,
    Optional,
    HostBinding,
    Output,
    EventEmitter,
    ViewChild,
    AfterViewInit
} from '@angular/core';
import {FormGroupDirective} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {TRANSLATION_BASE} from 'app/constants/injection-tokens';
import {faInfoCircle} from '@fortawesome/free-solid-svg-icons';
import {DiFormElement} from 'app/common/di-forms/shared/di-form-element';
import {DiFormConfigDirective} from 'app/common/di-forms/directives/di-form.directive';
import {ErrorStateMatcher} from '@angular/material/core';
import {RoleService} from 'app/blocks/service/api/role.service';
import {NzTimePickerComponent} from 'ng-zorro-antd/time-picker';
import {CandyDate} from 'ng-zorro-antd/core/time';
import {LogService} from 'app/blocks/service/log.service';
import {debounce} from 'lodash';

export const formElementProvider: any = {
    provide: DiFormElement,
    useExisting: forwardRef(() => DiTimeComponent)
};

// [(ngModel)]="time"
// [ngModelOptions]="{standalone: true}"
// (ngModelChange)="onChange($event)"

@Component({
    selector: 'di-time',
    template: `
        <nz-form-item [formGroup]="thisFormGroup" [ngStyle]="{'justify-content': getPosition()}" *ngIf="this.showField">
            <div [ngStyle]="{width: width}">
                <nz-form-label *ngIf="!suppressLabel" [nzFor]="fieldLabel$ | async" [nzRequired]="fieldIsRequired" [nzNoColon]="true">
                    <span>
                        {{ useCustomFieldLabel ? customFieldLabel : (fieldLabel$ | async) }}
                        <ng-container *ngIf="fieldHint$ | async as hint"
                            ><fa-icon class="di-mhint-icon" [icon]="faInfoIcon" size="xs" nz-tooltip [nzTooltipTitle]="hint"></fa-icon
                            ><span class="di-mfield-hint">{{ hint }}</span></ng-container
                        >
                    </span>
                </nz-form-label>
                <nz-form-control [nzErrorTip]="suppressError ? null : (validationError$ | async)">
                    <nz-time-picker
                        #timePicker
                        id="{{ 'di-time-' + field + idSuffix }}"
                        [ngStyle]="{width: width, 'max-width': width}"
                        [nzAutoFocus]="focus"
                        formControlName="{{ field }}"
                        nzFormat="HH:mm"
                        type="time"
                        [nzUse12Hours]="false"
                        [nzMinuteStep]="5"
                        [required]="fieldIsRequired"
                        [nzDisabled]="disabled"
                        [nzAllowEmpty]="!disabled && allowClear"
                        (ngModelChange)="onChange($event)"
                        (nzOpenChange)="onOpenChange($event)"
                        (keydown.Tab)="onTab($event)">
                    </nz-time-picker>
                </nz-form-control>
            </div>
        </nz-form-item>
    `,
    styleUrls: ['./di-time.component.scss'],
    providers: [formElementProvider],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DiTimeComponent extends DiFormElement implements OnInit, AfterViewInit {
    @Input() @HostBinding('class.flex-grow') grow = true;
    @Input() width = '100%';
    @Input() align: 'start' | 'center' | 'end' = 'start';
    @Input() focus = false;
    @Input() idSuffix: string | number = '';

    @Input() field: string;
    @Input() suppressLabel = false;
    @Input() suppressError = false;
    @Input() customErrorStateMatcher: ErrorStateMatcher;
    @Input() readonly = false;
    @Input() customFieldLabel: string;
    @Input() useCustomFieldLabel = false;
    @Input() roleDependent = false;
    @Input() showField = true;
    @Input() roleSettingSuffix = '';
    @Input() disabled = false;
    @Input() allowClear = true;
    @Input() enableTracking = false;
    @Input() onTab: (event) => any = (event) => this.onTabDefault(event);

    @Output() selectionChange = new EventEmitter<any>();

    uq = Date.now() + Math.floor(Math.random() * 100000);
    faInfoIcon = faInfoCircle;
    time: Date = null;

    @ViewChild('timePicker') timePicker: NzTimePickerComponent;

    constructor(
        @Inject(TRANSLATION_BASE) translationBase,
        translateService: TranslateService,
        fgDirective: FormGroupDirective,
        @Optional() diForm: DiFormConfigDirective,
        private _logService: LogService,
        protected roleService: RoleService
    ) {
        super(translationBase, translateService, fgDirective, roleService, diForm);
    }

    getFieldName(): string {
        return this.field;
    }

    getPosition(): string {
        if (this.align !== 'center') {
            return 'flex-' + this.align;
        }
        return this.align;
    }

    onChange(date: Date | null): void {
        if (date) {
            this.selectionChange.emit(date);
        }
    }

    ngAfterViewInit(): void {
        this.timePicker.onInputChange = this.onInputChange;
    }

    private debouncedLog = debounce((value) => {
        this._logService.log('Time Change', {
            field: this._fieldPath,
            value: value
        });
    }, 500);

    onInputChange(date) {
        if (typeof date === 'string') {
            const isFormattedDate = date.includes(':');
            // handle both raw HHmm and formatted HH:mm formats
            if ((date.length === 4 && !isFormattedDate) || (date.length === 5 && isFormattedDate)) {
                const hour = parseInt(date.slice(0, 2), 10);
                const minute = parseInt(isFormattedDate ? date.slice(3, 5) : date.slice(2, 4), 10);

                if (!isNaN(hour) && !isNaN(minute)) {
                    console.log(hour, minute);
                    const date = new Date();
                    date.setHours(hour, minute);
                    this.setValue(date);
                }
                if (this.enableTracking) {
                    this.debouncedLog(date);
                }
            }
        }
    }

    writeValue(value: any): void {
        // Implement the writeValue method here
    }

    setValue(val) {}

    onOpenChange(event) {
        if (event) {
            this.timePicker.inputRef.nativeElement.select();
        }
    }

    onTabDefault(event?) {
        const inputValue = this.timePicker.inputValue;
        if (typeof inputValue === 'string') {
            const padded = inputValue.padStart(4, '0');
            this.timePicker.onInputChange(padded);
            this.timePicker.closePanel();
        }
    }
}
